import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  MergeType as MergeIcon,
  Warning as WarningIcon,
  TrendingUp as ProjectionsIcon,
  Analytics as MetricsIcon,
  AutoAwesome as AIIcon,
  HourglassEmpty as WaitingIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { auth } from '../../../../../../firebase';
import { getApiBase } from '../../../../../../utils/apiConfig';
import { PROCESSING_ENDPOINTS } from '../../../constants/serviceEndpoints';
import apiClient from '../../../../../../utils/apiClient';

// ─── Auth helper (matches SchoolReprocessor pattern) ─────────────────────────
const getAuthHeaders = async () => {
  const user = auth.currentUser;
  if (!user) return {};
  try {
    const token = await user.getIdToken();
    return { Authorization: `Bearer ${token}` };
  } catch {
    return {};
  }
};

// ─── Post-processing steps shown in the success screen ───────────────────────
const POST_PROCESSING_STEPS = [
  { key: 'projections', label: 'Enrollment Projections', icon: <ProjectionsIcon /> },
  { key: 'metrics', label: 'School Metrics', icon: <MetricsIcon /> },
  { key: 'ai_summaries', label: 'AI Summaries', icon: <AIIcon /> },
];

// Status values for each post-processing step
const STATUS = { idle: 'idle', running: 'running', done: 'done', failed: 'failed' };

// ─── Component ────────────────────────────────────────────────────────────────
const MergeConfirmStep = ({ workflowData, onDataUpdate, isProcessing, setIsProcessing }) => {
  const { enqueueSnackbar } = useSnackbar();

  // Post-processing state
  const [postStatus, setPostStatus] = useState({
    projections: STATUS.idle,
    metrics: STATUS.idle,
    ai_summaries: STATUS.idle,
  });
  const [reprocessResult, setReprocessResult] = useState(null);
  const [reprocessError, setReprocessError] = useState(null);
  // AI summary polling: null | 'generating' | 'complete' | 'timeout'
  const [aiSummaryStatus, setAiSummaryStatus] = useState(null);

  const [executeError, setExecuteError] = useState(null);

  const preview = workflowData.previewData;
  const mergeResult = workflowData.mergeResult;
  const source = workflowData.sourceSchool;
  const survivor = workflowData.survivorSchool;

  // ── After merge succeeds, auto-run post-processing ──────────────────────────
  useEffect(() => {
    if (!mergeResult?.success) return;
    if (reprocessResult !== null || reprocessError !== null) return; // already ran

    runPostProcessing();
  }, [mergeResult?.success]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Poll for AI summary completion ──────────────────────────────────────────
  useEffect(() => {
    const uuid = survivor?.uuid;
    const wasRequested = reprocessResult?.overall_success;
    if (!uuid || !wasRequested) return;

    const reprocessedAt = reprocessResult?.reprocessing_completed_at
      ? new Date(reprocessResult.reprocessing_completed_at)
      : new Date();

    setAiSummaryStatus('generating');
    let done = false;

    const checkStatus = async () => {
      if (done) return;
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${getApiBase()}/v2/schools/${uuid}/ai-summary/status`, {
          headers,
          credentials: 'include',
        });
        if (resp.ok) {
          const data = await resp.json();
          const hasNewSummaries = Object.values(data.status || {}).some((byType) =>
            Object.values(byType).some(
              (s) => s?.generated_at && new Date(s.generated_at) > reprocessedAt
            )
          );
          if (hasNewSummaries) {
            done = true;
            setAiSummaryStatus('complete');
            setPostStatus((prev) => ({ ...prev, ai_summaries: STATUS.done }));
            clearInterval(intervalId);
            clearTimeout(timeoutId);
          }
        }
      } catch {
        // silently retry
      }
    };

    const intervalId = setInterval(checkStatus, 15000);
    const timeoutId = setTimeout(() => {
      if (!done) {
        clearInterval(intervalId);
        setAiSummaryStatus('timeout');
        setPostStatus((prev) => ({
          ...prev,
          ai_summaries: prev.ai_summaries === STATUS.running ? STATUS.done : prev.ai_summaries,
        }));
      }
    }, 600000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [reprocessResult?.overall_success]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Execute the merge ────────────────────────────────────────────────────────
  const handleExecute = async () => {
    setIsProcessing(true);
    setExecuteError(null);
    try {
      const result = await apiClient.post('/v2/merge/schools/execute', {
        source_uuid: source.uuid,
        survivor_uuid: survivor.uuid,
      });
      onDataUpdate({ mergeResult: result });
      enqueueSnackbar('Merge completed successfully', { variant: 'success' });
    } catch (err) {
      setExecuteError(err.message || 'Merge failed. Please try again.');
      enqueueSnackbar('Merge failed', { variant: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Post-processing: projections + metrics + ai_summaries ───────────────────
  const runPostProcessing = async () => {
    const uuid = survivor?.uuid;
    if (!uuid) return;

    setPostStatus({ projections: STATUS.running, metrics: STATUS.idle, ai_summaries: STATUS.idle });

    try {
      const authHeaders = await getAuthHeaders();

      const isLocal =
        process.env.NODE_ENV === 'development' ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';

      const apiUrl = isLocal
        ? `http://localhost:5001/api${PROCESSING_ENDPOINTS.REPROCESSING}`
        : `${getApiBase()}${PROCESSING_ENDPOINTS.REPROCESSING}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 900000);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({
          school_uuid: uuid,
          services: ['projections', 'metrics', 'ai_summaries'],
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Reprocessing returned ${response.status}`);
      }

      const result = await response.json();
      setReprocessResult(result);

      if (result.overall_success) {
        setPostStatus({
          projections: STATUS.done,
          metrics: STATUS.done,
          ai_summaries: STATUS.running, // polling will update this
        });
        enqueueSnackbar('Projections and metrics updated', { variant: 'success' });
      } else {
        setPostStatus({ projections: STATUS.failed, metrics: STATUS.failed, ai_summaries: STATUS.failed });
        setReprocessError(result.error || 'Post-processing failed');
      }
    } catch (err) {
      const message = err.name === 'AbortError'
        ? 'Post-processing timed out. It may still be running in the background.'
        : err.message || 'Post-processing failed';
      setReprocessError(message);
      setPostStatus({ projections: STATUS.failed, metrics: STATUS.failed, ai_summaries: STATUS.failed });
    }
  };

  // ── Status icon helper ───────────────────────────────────────────────────────
  const stepIcon = (statusKey) => {
    switch (postStatus[statusKey]) {
      case STATUS.running: return <CircularProgress size={20} />;
      case STATUS.done:    return <CheckIcon color="success" />;
      case STATUS.failed:  return <WarningIcon color="error" />;
      default:             return <WaitingIcon color="disabled" />;
    }
  };

  // ── Success screen (after merge) ─────────────────────────────────────────────
  if (mergeResult?.success) {
    const postProcessingRunning = Object.values(postStatus).some((s) => s === STATUS.running);
    const postProcessingDone = Object.values(postStatus).every(
      (s) => s === STATUS.done || s === STATUS.failed
    );

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <CheckIcon color="success" sx={{ fontSize: 48 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
              Merge Complete
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {mergeResult.message}
            </Typography>
          </Box>
        </Box>

        {/* Migration counts */}
        <Card sx={{ mb: 3, borderColor: 'success.main', borderWidth: 1, borderStyle: 'solid' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Records Transferred
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  {[
                    ['Enrollment rows', mergeResult.rows_migrated.enrollment],
                    ['FRL rows', mergeResult.rows_migrated.frl],
                    ['Grades offered rows', mergeResult.rows_migrated.grades_offered],
                    ['Directory entries', mergeResult.rows_migrated.directory],
                    ['Name records', mergeResult.rows_migrated.names],
                  ].map(([label, count]) => (
                    <TableRow key={label}>
                      <TableCell>{label}</TableCell>
                      <TableCell align="right">
                        <Chip label={count} color="success" size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Post-processing status */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Post-Merge Processing
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {reprocessError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Post-processing issue</AlertTitle>
                {reprocessError}
              </Alert>
            )}

            <Stepper orientation="vertical" nonLinear>
              {POST_PROCESSING_STEPS.map(({ key, label, icon }) => (
                <Step key={key} active completed={postStatus[key] === STATUS.done}>
                  <StepLabel
                    icon={stepIcon(key)}
                    optional={
                      postStatus[key] === STATUS.running ? (
                        <Typography variant="caption" color="primary">Running…</Typography>
                      ) : postStatus[key] === STATUS.done ? (
                        <Typography variant="caption" color="success.main">Complete</Typography>
                      ) : postStatus[key] === STATUS.failed ? (
                        <Typography variant="caption" color="error">Failed</Typography>
                      ) : (
                        <Typography variant="caption" color="text.disabled">Waiting</Typography>
                      )
                    }
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {icon}
                      <Typography variant="body1">{label}</Typography>
                    </Box>
                  </StepLabel>
                  {key === 'ai_summaries' && postStatus[key] === STATUS.running && (
                    <StepContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                        }}
                      >
                        {aiSummaryStatus === 'generating' && (
                          <>
                            <CircularProgress size={18} sx={{ mt: 0.2, flexShrink: 0 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                AI Summaries generating in background
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                All 12 summaries (4 types × 3 drive times) are being generated. This typically takes 5–10 minutes. This page will update automatically.
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Box>
                    </StepContent>
                  )}
                  {key === 'ai_summaries' && aiSummaryStatus === 'complete' && (
                    <StepContent>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                        AI Summaries ready — visible in the AI Summary tool.
                      </Typography>
                    </StepContent>
                  )}
                  {key === 'ai_summaries' && aiSummaryStatus === 'timeout' && (
                    <StepContent>
                      <Typography variant="body2" color="text.secondary">
                        AI Summaries are still generating. Check the AI Summary tool in a few minutes.
                      </Typography>
                    </StepContent>
                  )}
                </Step>
              ))}
            </Stepper>

            {postProcessingRunning && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">
                    Post-processing in progress — this may take a few minutes. Do not close this page.
                  </Typography>
                </Box>
              </Alert>
            )}

            {postProcessingDone && !reprocessError && (
              <Alert severity="success" sx={{ mt: 2 }}>
                All post-merge processing complete for{' '}
                <strong>{survivor?.display_name || survivor?.name}</strong>.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Pre-execution confirmation screen ────────────────────────────────────────
  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MergeIcon color="error" />
        Confirm & Execute Merge
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review the final summary below. Once you click <strong>Execute Merge</strong>, this action
        cannot be undone.
      </Typography>

      {/* Merge summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Merge Summary
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" color="warning.main" gutterBottom>
                Source (will be deactivated)
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {source?.display_name || source?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                NCES: {source?.ncessch}
              </Typography>
              {preview && (
                <Typography variant="body2" color="text.secondary">
                  Years: {preview.year_ranges.source_enrollment.join(', ') || 'none'}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MergeIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                Survivor (keeps its NCES ID)
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {survivor?.display_name || survivor?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                NCES: {survivor?.ncessch}
              </Typography>
              {preview && (
                <Typography variant="body2" color="text.secondary">
                  Current years: {preview.year_ranges.survivor_enrollment.join(', ') || 'none'}
                </Typography>
              )}
            </Grid>
          </Grid>

          {preview && (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" gutterBottom>
                What will happen:
              </Typography>
              <Grid container spacing={1}>
                {[
                  { label: 'Enrollment rows', count: preview.rows_to_migrate.enrollment },
                  { label: 'FRL rows', count: preview.rows_to_migrate.frl },
                  { label: 'Grades offered rows', count: preview.rows_to_migrate.grades_offered },
                  { label: 'Directory entries', count: preview.rows_to_migrate.directory },
                  { label: 'Name records', count: preview.rows_to_migrate.names },
                ].map(({ label, count }) => (
                  <Grid item key={label}>
                    <Chip
                      label={`${count} ${label}`}
                      size="small"
                      color={count > 0 ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </Grid>
                ))}
              </Grid>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                …will be transferred from{' '}
                <strong>{source?.display_name || source?.name}</strong> to{' '}
                <strong>{survivor?.display_name || survivor?.name}</strong>.
                The source school will be set to <strong>inactive</strong>.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* What happens after the merge */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Automatic Post-Merge Processing
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Immediately after the merge, the following will run automatically on{' '}
            <strong>{survivor?.display_name || survivor?.name}</strong>:
          </Typography>
          <List dense>
            {POST_PROCESSING_STEPS.map(({ key, label, icon }) => (
              <ListItem key={key} disablePadding sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>{icon}</ListItemIcon>
                <ListItemText primary={label} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 3 }}>
        <AlertTitle>This action cannot be undone</AlertTitle>
        <Typography variant="body2">
          <strong>{source?.display_name || source?.name}</strong> will be permanently deactivated.
          Ensure you have selected the correct source and survivor before proceeding.
        </Typography>
      </Alert>

      {executeError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Merge Failed</AlertTitle>
          {executeError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="contained"
          color="error"
          size="large"
          disabled={isProcessing}
          onClick={handleExecute}
          startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <MergeIcon />}
        >
          {isProcessing ? 'Merging…' : 'Execute Merge'}
        </Button>
        {isProcessing && (
          <Typography variant="body2" color="text.secondary">
            Transferring records — please wait…
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MergeConfirmStep;
